import { createSelector } from '@reduxjs/toolkit';
import { moduleNamespace } from '@Store/config.store';
import { NamespacedStoreState } from '@Types/store.types';
import { useSelector } from 'react-redux';
import { DeteriorationModelCard } from '@Types';

const parseNamespace = (state: NamespacedStoreState) => state[moduleNamespace];

const deteriorationModelsStoreSelector = createSelector(parseNamespace, state => state.DeteriorationModelsStore);

export const deteriorationModelsCardsSelector = createSelector(
	deteriorationModelsStoreSelector,
	deteriorationModelsStoreSelector => ({
		status: deteriorationModelsStoreSelector.deteriorationModelsList.status,
		pagination: deteriorationModelsStoreSelector.deteriorationModelsList.pagination,
		cards: deteriorationModelsStoreSelector.deteriorationModelsList.cards as DeteriorationModelCard[],
		isLoading: deteriorationModelsStoreSelector.deteriorationModelsList.isLoading,
		isWaiting: deteriorationModelsStoreSelector.deteriorationModelsList.isWaiting,
	}),
);

export const selectDeteriorationModelsCards = () => useSelector(deteriorationModelsCardsSelector);

export const deteriorationModelSelector = createSelector(
	deteriorationModelsStoreSelector,
	deteriorationModelsStoreSelector => deteriorationModelsStoreSelector.selectedDeteriorationModel,
);

export const selectDeteriorationModel = () => useSelector(deteriorationModelSelector);

export const deteriorationModelCurveConfigsSelector = createSelector(
	deteriorationModelsStoreSelector,
	deteriorationModelsStoreSelector => ({
		status: deteriorationModelsStoreSelector.deteriorationModelCurveConfigsList.pagination.status,
		pagination: deteriorationModelsStoreSelector.deteriorationModelCurveConfigsList.pagination.pagination,
		rows: deteriorationModelsStoreSelector.deteriorationModelCurveConfigsList.pagination.rows,
		isLoading: deteriorationModelsStoreSelector.deteriorationModelCurveConfigsList.pagination.isLoading,
		isWaiting: deteriorationModelsStoreSelector.deteriorationModelCurveConfigsList.pagination.isWaiting,
	}),
);

export const selectDeteriorationModelCurveConfigs = () => useSelector(deteriorationModelCurveConfigsSelector);

export const curveConfigsSelector = createSelector(
	deteriorationModelsStoreSelector,
	deteriorationModelsStoreSelector => deteriorationModelsStoreSelector.deteriorationModelCurveConfigsList.list,
);

export const selectCurveConfig = (curveConfigId: string) =>
	useSelector((state: NamespacedStoreState) => {
		const curveConfigs = curveConfigsSelector(state);
		return curveConfigs.find(config => config.curveConfigId === curveConfigId) || null;
	});

export const industryStandardCurvesSelector = createSelector(
	deteriorationModelsStoreSelector,
	deteriorationModelsStoreSelector => deteriorationModelsStoreSelector.industryStandardCurves,
);

export const selectIndustryStandardCurves = () => useSelector(industryStandardCurvesSelector);

export const deteriorationModelAssetsResultsSelector = createSelector(
	deteriorationModelsStoreSelector,
	deteriorationModelsStoreSelector => ({
		status: deteriorationModelsStoreSelector.assetResults.status,
		pagination: deteriorationModelsStoreSelector.assetResults.pagination,
		rows: deteriorationModelsStoreSelector.assetResults.rows,
		isLoading: deteriorationModelsStoreSelector.assetResults.isLoading,
		isWaiting: deteriorationModelsStoreSelector.assetResults.isWaiting,
		request: deteriorationModelsStoreSelector.assetResultsRequest,
	}),
);

export const selectDeteriorationModelAssetsResults = () => useSelector(deteriorationModelAssetsResultsSelector);

export const deteriorationModelGroupsResultsSelector = createSelector(
	deteriorationModelsStoreSelector,
	deteriorationModelsStoreSelector => ({
		status: deteriorationModelsStoreSelector.groupResults.status,
		pagination: deteriorationModelsStoreSelector.groupResults.pagination,
		rows: deteriorationModelsStoreSelector.groupResults.rows,
		isLoading: deteriorationModelsStoreSelector.groupResults.isLoading,
		isWaiting: deteriorationModelsStoreSelector.groupResults.isWaiting,
	}),
);

export const selectDeteriorationModelGroupsResults = () => useSelector(deteriorationModelGroupsResultsSelector);

export const globalUndefinedCountSelector = createSelector(
	deteriorationModelsStoreSelector,
	deteriorationModelsStoreSelector => deteriorationModelsStoreSelector.globalUndefinedCount,
);

export const selectGlobalUndefinedCount = () => useSelector(globalUndefinedCountSelector);

export const paginationStateSelector = createSelector(
	deteriorationModelsStoreSelector,
	deteriorationModelsStoreSelector => deteriorationModelsStoreSelector.paginationState,
);

export const selectPaginationState = () => useSelector(paginationStateSelector);

export const selectModelPaginationState = (modelId: string) =>
	useSelector((state: NamespacedStoreState) => {
		const paginationState = paginationStateSelector(state);
		return paginationState[modelId] || null;
	});

/** The grade card whose breakdown is open for this model, or '' when none is. */
export const selectSelectedBreakdownGrade = (modelId: string) =>
	useSelector(
		(state: NamespacedStoreState) => deteriorationModelsStoreSelector(state).selectedBreakdownGrade?.[modelId] ?? '',
	);
